import {
  afterNextRender,
  ChangeDetectorRef,
  Injector,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import {DropdownButtonComponent} from '@/app/shared/components/buttons/dropdown-button/dropdown-button.component';
import {ButtonSimpleComponent} from '@/app/shared/components/buttons/button-simple/button-simple.component';
import {NgClass, NgOptimizedImage} from '@angular/common';
import {Router, RouterLink} from '@angular/router';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import {LangSelectorComponent} from '@/app/shared/components/lang-selector/lang-selector.component';
import {AuthService} from '@services/auth.service';
import {environment} from '@/environments/environment';
import {
	NavbarProfileButtonComponent, NavbarProfileItem
} from '@components/navbar/navbar-profile-button/navbar-profile-button.component';
import {NotificationCenterComponent} from '@components/notification-center/notification-center.component';
import {FeatureFlagDirective} from '@/app/shared/directives/feature-flag.directive';
import {FeatureFlagKey} from '@/app/core/models/feature-flag/featureFlagKey';
import {
	CategoryDropdownButtonComponent
} from '@/app/shared/components/buttons/category-dropdown-button/category-dropdown-button.component';
import {StreamService} from '@services/stream.service';
import {Streamer} from '@/app/core/models/streamer';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {map} from 'rxjs/operators';
import {isTicketingOpen, TICKETING_URL} from '@/app/config/ticketing.config';

export interface DropdownItem {
	label: string;
	action: () => void;
	image?: string;
	featureFlag?: FeatureFlagKey;
	order: number
}

export interface CategoryDropdownItem {
	label: string;
	items: DropdownItem[];
}

@Component({
  selector: 'peps-navbar',
	imports: [
		DropdownButtonComponent,
		ButtonSimpleComponent,
		NgClass,
		TranslatePipe,
		NgOptimizedImage,
		LangSelectorComponent,
		NavbarProfileButtonComponent,
		NotificationCenterComponent,
		FeatureFlagDirective,
		CategoryDropdownButtonComponent,
	],
	styles: [`
		@keyframes retro-shadow-flash {
			0%, 100% {
				background-color: #EEC932;
			}
			50% {
				background-color: #c96527;
				color: #fff;
			}
		}

		.animate-retro-flash {
			animation: retro-shadow-flash 0.6s steps(1) infinite;
		}
		@keyframes heartbeat {
			0%, 100% { transform: scale(1); }
			25% { transform: scale(1.06); }
			50% { transform: scale(1); }
			75% { transform: scale(1.06); }
		}

		.animate-heartbeat {
			animation: heartbeat 1.2s infinite ease-in-out,retro-shadow-flash 0.6s steps(1) infinite;
		}
		.promo-explosion {
			clip-path: polygon(
				50% 0%, 58% 15%, 72% 3%, 73% 20%, 88% 12%, 83% 28%, 98% 27%, 88% 41%,
				100% 50%, 88% 59%, 98% 73%, 83% 72%, 88% 88%, 73% 80%, 72% 97%, 58% 85%,
				50% 100%, 42% 85%, 28% 97%, 27% 80%, 12% 88%, 17% 72%, 2% 73%, 12% 59%,
				0% 50%, 12% 41%, 2% 27%, 17% 28%, 12% 12%, 27% 20%, 28% 3%, 42% 15%
			);
		}
	`],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './navbar.component.html',
})
export class NavbarComponent implements OnInit, OnDestroy {

	private readonly router: Router = inject(Router);
	protected readonly translate: TranslateService = inject(TranslateService);
	private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
	protected readonly authService: AuthService = inject(AuthService);
	private readonly streamService: StreamService = inject(StreamService);
	private readonly destroyRef: DestroyRef = inject(DestroyRef);
private readonly injector: Injector = inject(Injector);

	// ── Bouton billetterie : affiché jusqu'au 31/10/2026 à 23h00 (heure locale) ──
	protected readonly ticketingUrl = TICKETING_URL;
	readonly showTicketing = signal(isTicketingOpen());

	isOpen: WritableSignal<boolean> = signal(false);
	streamersOnline: WritableSignal<Streamer[]> = signal([]);
	streamersOffline: WritableSignal<Streamer[]> = signal([]);
	mainStreamer: WritableSignal<Streamer | null> = signal(null);

	// ── Pastille "live" sur le bouton du drawer Twitch ──
	// Masquée après ouverture du drawer, mais réapparaît si l'ensemble des lives
	// change (nouveau streamer en ligne) ou après expiration du TTL.
	private static readonly LIVE_BADGE_SEEN_KEY = 'twitch-live-badge-seen';
	private static readonly LIVE_BADGE_SEEN_TTL_MS = 4 * 60 * 60 * 1000; // 4 h
	private readonly liveBadgeSeen = signal<{ sig: string; ts: number } | null>(this.readLiveBadgeSeen());

	/** Nombre de chaînes en direct (streamers + chaîne principale). */
	readonly liveCount = computed(
		() => this.streamersOnline().length + (this.mainStreamer()?.isLive ? 1 : 0),
	);

	/** Empreinte de l'ensemble courant des lives — change dès qu'un streamer arrive/part. */
	private readonly liveSignature = computed(() => {
		const pseudos = this.streamersOnline().map(s => s.pseudo);
		const main = this.mainStreamer();
		if (main?.isLive) pseudos.push(main.pseudo);
		return pseudos.sort().join('|');
	});

	/** Affiche la pastille si des lives ET (jamais vue, OU set changé depuis, OU TTL dépassé). */
	readonly showLiveBadge = computed(() => {
		if (this.liveCount() === 0) return false;
		const seen = this.liveBadgeSeen();
		if (!seen) return true;
		if (seen.sig !== this.liveSignature()) return true;
		return Date.now() - seen.ts > NavbarComponent.LIVE_BADGE_SEEN_TTL_MS;
	});

	isMobileMenuOpen: boolean = false;

	itemsDropdownLang = [
		{ label: 'FR', action: () => this.useLanguage('fr'), image: 'assets/icons/flags/flag_fr.svg' },
		{ label: 'EN', action: () => this.useLanguage('en'), image: 'assets/icons/flags/flag_en.svg' },
	];

	itemsDropdownRoster: DropdownItem[] = [];
	itemsDropdownProfile: NavbarProfileItem[] = [];
	itemsDropdownClub: DropdownItem[] = [];
	itemsDropdownGames: CategoryDropdownItem[] = [];

	langChangeSub!: Subscription;

	isAuthEnabled: boolean = false;

	ngOnInit(): void {
		this.checkIsAuthEnabled();
		this.initDropdowns()
		this.langChangeSub = this.translate.onLangChange.subscribe(() => {
			this.initDropdowns();
		});
		// Navigateur uniquement, après hydratation : le live Twitch change sans cesse,
		// un rendu SSR différent du premier rendu client provoque NG0500.
		afterNextRender(() => this.loadStreamers(), {injector: this.injector});

    }

	ngOnDestroy(): void {
		if (this.langChangeSub) {
			this.langChangeSub.unsubscribe();
		}
	}

	useLanguage(lang: string) {
		this.translate.use(lang);
		localStorage.setItem('lang', lang);
		this.cdr.detectChanges();
	}

	initDropdowns(): void {
		this.itemsDropdownRoster = [
			{
				label: this.translate.instant('component.navbar.team.overwatch.pro'),
				action: () => this.toOverwatchPro(),
				image: 'assets/images/max_button/max_button_overwatch.png',
				order: 1,
			},
			{
				label: this.translate.instant('component.navbar.team.overwatch.academy'),
				action: () => this.toOverwatchAcademy(),
				image: 'assets/images/peps_academy_logo.png',
				featureFlag: 'OVERWATCH_ACADEMY',
				order: 2,
			},
			{
				label: this.translate.instant('component.navbar.team.marvel-rivals'),
				action: () => this.toMarvelRivals(),
				image: 'assets/icons/Marvel_Rivals_logo.svg',
				featureFlag: 'MARVEL_RIVALS',
				order: 3,
			},
			{
				label: this.translate.instant('component.navbar.team.pokemon-vgc'),
				action: () => this.toPokemonVgc(),
				image: 'assets/icons/pokemon_vgc_logo.svg',
				featureFlag: 'POKEMON_VGC',
				order: 4,
			},
			{
				label: this.translate.instant('component.navbar.team.pokemon-unite'),
				action: () => this.toPokemonUnite(),
				image: 'assets/icons/pokemon_vgc_logo.svg',
				featureFlag: 'POKEMON_UNITE',
				order: 5,
			},
			{
				label: this.translate.instant('component.navbar.team.dofus'),
				action: () => this.toDofus(),
				image: 'assets/images/logo/dofus.png',
				order: 6
			},
			{
				label: this.translate.instant('component.navbar.team.sport'),
				action: () => this.toSport(),
				image: 'assets/icons/sport.svg',
				order: 7
			}
		];
		this.itemsDropdownProfile = [
			{ label: this.translate.instant('component.navbar.profile'), action: () => this.toProfile() },
			{ label: this.translate.instant('component.navbar.logout'), action: () => this.authService.logout() },
			{
				label: 'FR',
				action: () => this.useLanguage('fr'),
				image: 'assets/icons/flags/flag_fr.svg',
				isSeparator: true,
				isActive: this.translate.currentLang === 'fr',
			},
			{
				label: 'EN',
				action: () => this.useLanguage('en'),
				image: 'assets/icons/flags/flag_en.svg',
				isActive: this.translate.currentLang === 'en',
			},
		]

		this.itemsDropdownClub = [
			{ label: this.translate.instant('component.navbar.club.club'), action: () => this.toClub(), order: 1 },
			{ label: this.translate.instant('component.navbar.club.news'), action: () => this.toNews(), order: 2 },
			{ label: this.translate.instant('component.navbar.club.gallery'), action: () => this.toGallery(), order: 3 },
			{
				label: this.translate.instant('common.pplace'),
				action: () => this.toPlace(),
				featureFlag: 'P_PLACE',
				order: 4
			}
		]

		this.itemsDropdownGames = [
			{
				label: this.translate.instant('common.bet'),
				items: [
					{ label: this.translate.instant('component.navbar.bet.betting'), action: () => this.toBet(), order: 1 },
					{ label: this.translate.instant('component.navbar.bet.leaderboard'), action: () => this.toLeaderBoard(), order: 2 },
				]
			},
			{
				label: this.translate.instant('common.quests'),
				items: [
					{
						label: this.translate.instant('component.navbar.quests.subtitle'),
						action: () => this.toQuests(),
						featureFlag: 'BATTLEPEPS',
						order: 1
					},
				]
			},
			{
				label: this.translate.instant('component.navbar.collection.title'),
				items: [
					{ label: this.translate.instant('component.navbar.collection.shop'), action: () => this.toShop(), order: 1 },
					{ label: this.translate.instant('component.navbar.collection.market'), action: () => this.toMarket(), order: 2 },
				]
			}
		]
	}

	get userPicture(): string {
		return this.authService.getUser()?.picture ?? '';
	}

	toggleMobileMenu() {
		this.isMobileMenuOpen = !this.isMobileMenuOpen;
		if (this.isMobileMenuOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
	}

	resetMobileMenu() {
		this.isMobileMenuOpen = false;
		document.body.style.overflow = '';
	}

	loginWithTwitch() {
		window.location.href = `${environment.backendUrl}/auth/twitch/authorize?origin=main`;
	}

	toNews() {
		this.router.navigate(['/news']);
		this.resetMobileMenu();
	}

	toAmbassadors() {
		this.router.navigate(['/ambassadors']);
		this.resetMobileMenu();
	}

	toPartners() {
		this.router.navigate(['/partners']);
		this.resetMobileMenu();
	}

	toMatches() {
		this.router.navigate(['/matchs']);
		this.resetMobileMenu();
	}

	toClub() {
		this.router.navigate(['/club']);
		this.resetMobileMenu();
	}

	toOverwatchPro() {
		this.router.navigate(['/roster/overwatch/pro']);
		this.resetMobileMenu();
	}

	toOverwatchAcademy() {
		this.router.navigate(['/roster/overwatch/academy']);
		this.resetMobileMenu();
	}

	toMarvelRivals() {
		this.router.navigate(['/roster/marvel-rivals']);
		this.resetMobileMenu();
	}

	toPokemonVgc() {
		this.router.navigate(['/roster/pokemon/vgc']);
		this.resetMobileMenu();
	}

	toGallery() {
		this.router.navigate(['/gallery']);
		this.resetMobileMenu();
	}

	toTipee() {
		window.open('https://fr.tipeee.com/teampeps', '_blank');
	}

	toProfile() {
		this.router.navigate(['/profil']);
		this.resetMobileMenu();
	}

	toBet() {
		this.router.navigate(['/bet/matches']);
		this.resetMobileMenu();
	}

	toLeaderBoard() {
		this.router.navigate(['/bet/leaderboard']);
		this.resetMobileMenu();
	}

	private checkIsAuthEnabled() {
		const targetDateParis = new Date('2025-09-03T18:59:59+02:00');

		const now = new Date();

		this.isAuthEnabled = now.getTime() >= targetDateParis.getTime();
	}

	protected toPlace() {
		this.router.navigate(['/place']);
		this.resetMobileMenu();
	}

	private toPokemonUnite() {
		this.router.navigate(['/roster/pokemon/unite']);
		this.resetMobileMenu();
	}

	protected toQuests() {
		this.router.navigate(['/battlepeps']);
		this.resetMobileMenu();
	}

	private toDofus() {
		this.router.navigate(['/roster/dofus']);
		this.resetMobileMenu();
	}

	protected openDrawer() {
		this.isOpen.set(true);
		this.markLiveBadgeSeen();
	}

	protected closeDrawer() {
		this.isOpen.set(false);
	}

	/** Mémorise l'empreinte des lives vus + l'instant, pour masquer la pastille jusqu'au prochain changement / TTL. */
	private markLiveBadgeSeen(): void {
		const entry = { sig: this.liveSignature(), ts: Date.now() };
		this.liveBadgeSeen.set(entry);
		try {
			localStorage.setItem(NavbarComponent.LIVE_BADGE_SEEN_KEY, JSON.stringify(entry));
		} catch {
			/* localStorage indisponible (mode privé, quota) : la pastille se re-masquera juste à chaque session */
		}
	}

	private readLiveBadgeSeen(): { sig: string; ts: number } | null {
		try {
			const raw = localStorage.getItem(NavbarComponent.LIVE_BADGE_SEEN_KEY);
			if (!raw) return null;
			const parsed = JSON.parse(raw) as { sig?: unknown; ts?: unknown };
			if (typeof parsed.sig === 'string' && typeof parsed.ts === 'number') {
				return { sig: parsed.sig, ts: parsed.ts };
			}
			return null;
		} catch {
			return null;
		}
	}

	private loadStreamers() {
		this.streamService.getStreamers().pipe(
			takeUntilDestroyed(this.destroyRef),
			map(streamers => {
				const main = streamers.find(s => s.pseudo === 'fefegg' && s.isLive) ?? null;

				const online = streamers
					.filter(s => s.isLive && s.pseudo !== 'fefegg')
					.sort((a, b) => b.viewerCount - a.viewerCount);

				const offline = streamers
					.filter(s => !s.isLive)
					.sort((a, b) => a.displayName.localeCompare(b.displayName));

				return { online, offline, main };
			})
		).subscribe(({ online, offline, main }) => {
			this.streamersOnline.set(online);
			this.streamersOffline.set(offline);
			this.mainStreamer.set(main);
		});
	}

	protected toShop() {
		this.router.navigate(['/collection/shop']);
		this.resetMobileMenu();
	}

	protected toMarket() {
		this.router.navigate(['/collection/marketplace']);
		this.resetMobileMenu();
	}

	protected toInventory() {
		this.router.navigate(['/inventory']);
		this.resetMobileMenu();
	}

	private toSport() {
		this.router.navigate(['/roster/sport']);
		this.resetMobileMenu();
	}
}
